import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface PromptTrackingInitialAnalysisRequest {
  project_id: string;
  prompts?: string[];
}

export interface PromptTrackingData {
  id: string;
  prompt_text: string;
  volume?: number;
  mentions: number;
  citations: number;
  impressions: number;
  ai_share_of_voice: number;
  collection_month?: string;
  collection_timestamp: string;
  created_at: string;
  updated_at: string;
}

export interface PromptTrackingListResponse {
  data: PromptTrackingData[];
  total: number;
  result_count: number;
}

export interface PromptDetail {
  prompt_text: string;
  ai_content: string;
  ai_overview?: string;
  mentions: string[];
  citations: Array<{ url: string; title: string }>;
  volume?: number;
  mentions_count: number;
  citations_count: number;
  impressions: number;
  ai_share_of_voice: number;
  snapshot_timestamp: string;
  is_live: boolean;
}

export interface CompositeScore {
  score: number;
  label: string;
  metrics: {
    mentions: number;
    citations: number;
    impressions: number;
    ai_share_of_voice: number;
  };
}

export interface MonthlyTrackingData {
  month: string;
  mentions: number;
  citations: number;
  impressions: number;
  ai_share_of_voice: number;
  volume: number;
  prompt_count: number;
}

export interface PromptTrackingDashboardResponse {
  composite_score: CompositeScore;
  tracking_data: PromptTrackingListResponse;
  monthly_data: MonthlyTrackingData[];
}

export interface PromptTrackingRefreshResponse {
  success: boolean;
  message: string;
  deleted_count: number;
  new_prompts_count: number;
}

export interface PromptTrackingTaskResponse {
  task_id: string;
  status: string;
  message: string;
}

export interface PromptTrackingTaskStatus {
  task_id: string;
  status: string;
  task_type: string;
  /** null until prompts are known; then 2 + total_prompts. Show "Preparing..." when null. */
  total_steps: number | null;
  total_prompts: number;
  completed_steps: number;
  completed_prompts: number;
  progress_percentage: number;
  current_step: string | null;
  estimated_time_remaining: number | null;
  error_message: string | null;
  result: any;
  /** ai_generated | uploaded | prompt_creation – how prompts were obtained (for UI). */
  prompt_source?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PromptTrackingService {
  private apiUrl = `${environment.apiUrl}/generic/prompt-tracking`;

  constructor(private http: HttpClient) {}

  // Deprecated: Initial analysis is now auto-triggered by the dashboard endpoint
  // Keeping this method for backward compatibility but it's no longer used
  // performInitialAnalysis(request: PromptTrackingInitialAnalysisRequest): Observable<{ data: PromptTrackingData[]; total: number }> {
  //   return this.http.post<{ data: PromptTrackingData[]; total: number }>(`${this.apiUrl}/initial-analysis`, request);
  // }

  getTrackingData(
    projectId: string,
    filterPeriod?: 'monthly' | 'quarterly' | 'semi-annual' | 'annual',
    startDate?: string,
    endDate?: string
  ): Observable<PromptTrackingListResponse> {
    let params = new HttpParams().set('project_id', projectId);
    if (filterPeriod) {
      params = params.set('filter_period', filterPeriod);
    }
    if (startDate) {
      params = params.set('start_date', startDate);
    }
    if (endDate) {
      params = params.set('end_date', endDate);
    }
    return this.http.get<PromptTrackingListResponse>(`${this.apiUrl}/data`, { params });
  }

  getCompositeScore(projectId: string): Observable<CompositeScore> {
    const params = new HttpParams().set('project_id', projectId);
    return this.http.get<CompositeScore>(`${this.apiUrl}/composite-score`, { params });
  }

  /**
   * Start prompt tracking analysis (task-based).
   * body: optional { prompts } from upload or { research_id } from Prompt Creation; omit for generate-from-website.
   */
  startPromptTrackingAnalysis(
    projectId: string,
    body?: { prompts?: string[]; research_id?: string }
  ): Observable<PromptTrackingTaskResponse> {
    const params = new HttpParams().set('project_id', projectId);
    return this.http.post<PromptTrackingTaskResponse>(`${this.apiUrl}/prompt-tracking`, body ?? {}, { params });
  }

  /** Validate uploaded prompts file (TXT or CSV). Returns prompts and any errors. */
  validateUploadPrompts(file: File): Observable<{ valid: boolean; prompts: string[]; errors?: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ valid: boolean; prompts: string[]; errors?: string[] }>(
      `${this.apiUrl}/validate-upload`,
      formData
    );
  }

  /**
   * Get task status for prompt tracking
   */
  getPromptTrackingStatus(taskId: string): Observable<PromptTrackingTaskStatus> {
    return this.http.get<PromptTrackingTaskStatus>(`${this.apiUrl}/prompt-tracking/${taskId}/status`);
  }

  /**
   * Get prompt tracking results for completed task
   */
  getPromptTrackingResult(taskId: string): Observable<PromptTrackingDashboardResponse> {
    return this.http.get<PromptTrackingDashboardResponse>(`${this.apiUrl}/prompt-tracking/${taskId}`);
  }

  /**
   * @deprecated Use startPromptTrackingAnalysis instead
   * Kept for backward compatibility
   */
  getDashboardData(projectId: string): Observable<PromptTrackingDashboardResponse> {
    const params = new HttpParams().set('project_id', projectId);
    return this.http.get<PromptTrackingDashboardResponse>(`${this.apiUrl}/dashboard`, { params });
  }

  getPromptDetail(
    promptId: string,
    startDate?: string,
    endDate?: string
  ): Observable<PromptDetail> {
    let params = new HttpParams();
    if (startDate) {
      params = params.set('start_date', startDate);
    }
    if (endDate) {
      params = params.set('end_date', endDate);
    }
    return this.http.get<PromptDetail>(`${this.apiUrl}/prompt/${promptId}`, { params });
  }


  exportTrackingData(
    projectId: string,
    filterPeriod?: string,
    startDate?: string,
    endDate?: string
  ): Observable<Blob> {
    let params = new HttpParams().set('project_id', projectId);
    if (filterPeriod) {
      params = params.set('filter_period', filterPeriod);
    }
    if (startDate) {
      params = params.set('start_date', startDate);
    }
    if (endDate) {
      params = params.set('end_date', endDate);
    }
    return this.http.get(`${this.apiUrl}/export`, {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Start refresh operation (task-based)
   * Returns PromptTrackingTaskResponse with task_id for status polling
   */
  refreshTrackingData(projectId: string): Observable<PromptTrackingTaskResponse> {
    const params = new HttpParams().set('project_id', projectId);
    return this.http.post<PromptTrackingTaskResponse>(`${this.apiUrl}/refresh`, {}, { params });
  }

  /**
   * Cancel a running prompt tracking task
   */
  cancelPromptTrackingTask(taskId: string): Observable<PromptTrackingTaskStatus> {
    return this.http.post<PromptTrackingTaskStatus>(`${this.apiUrl}/prompt-tracking/${taskId}/cancel`, {});
  }
}

